import { Code2, Globe } from 'lucide-react';
import type { FC, SVGProps } from 'react';
import { Images } from '@/assets/images';
import {
  PLATFORM_REGISTRY,
  type PlatformId,
} from '@kirin/api-client/contracts/platform-registry';

/**
 * Icon component type that works with both SVG icons and Lucide icons
 */
export type IconComponent = FC<SVGProps<SVGSVGElement>>;

/**
 * Known platform types for IDE clients.
 * Derived from the platform registry.
 */
export type Platform = PlatformId;

/**
 * Map from PlatformId to its icon component.
 * Every registry entry must have an icon here; the type enforces completeness.
 */
const PLATFORM_ICONS: Record<PlatformId, IconComponent> = {
  cursor: Images.CursorIcon as IconComponent,
  claude: Images.ClaudeIcon as IconComponent,
  claude_desktop: Images.ClaudeDesktopIcon as IconComponent,
  // KIR-3908: Cowork-hook provenance marker — reuse the Claude Desktop glyph.
  claude_desktop_hook: Images.ClaudeDesktopIcon as IconComponent,
  copilot: Images.CopilotIcon as IconComponent,
  // GitHub Copilot CLI (KIR-3179) — same GitHub Copilot brand as the IDE
  // entry, so it reuses the Copilot mark.
  github_copilot_cli: Images.CopilotIcon as IconComponent,
  vscode: Images.VSCodeIcon as IconComponent,
  // KIR-3873: Devin Desktop three-hexagon mark (currentColor, same as
  // Windsurf). Internal id stays `windsurf`; displayName rename is PR 3.
  windsurf: Images.DevinIcon as IconComponent,
  // KIR-2217: no branded Antigravity SVG exists yet. Map to lucide `Code2`
  // (same glyph `getPlatformIcon` uses for unknown/unreleased platforms)
  // rather than inventing a stand-in that resembles another product's mark.
  // When design ships the real brand SVG, drop it in `assets/images/`,
  // import here as `Images.AntigravityIcon`, and swap this line.
  antigravity: Code2 as unknown as IconComponent,
  gemini_cli: Images.GeminiCliIcon as IconComponent,
  // KIR-3334: branded Codex app icon (converted from the ticket's app.icns to
  // assets/images/codex.png, wrapped by CodexIcon).
  codex: Images.CodexIcon as IconComponent,
  // KIR-4646: shared `.agents/skills` owner, not a product — no brand mark.
  multi_tool: Code2 as unknown as IconComponent,
  // KIR-4703: browser-agent activity is a provenance marker (like
  // claude_desktop_hook), not an installable agent. No branded SVG — use the
  // lucide Globe glyph for "Browser AI Agent" so alert rows with this Source
  // render an icon instead of crashing on an undefined component.
  browser_agent: Globe as unknown as IconComponent,
};

/**
 * KIR-4646: what the Multi-IDE / Platform label means. Same sentence as the
 * agent CLI's `(multi-tool)` legend line.
 */
export const MULTI_TOOL_TOOLTIP =
  'Generic agent-config file. Used by more than one IDE or platform.';

/**
 * Display labels for platforms — always the registry `displayName`. Visibility
 * gating (whether a platform's data surfaces at all for a given org) is handled
 * upstream; once data reaches a chip, the label must be the canonical product
 * name regardless of feature-flag state.
 */
export const platformLabels: Record<PlatformId, string> = Object.fromEntries(
  Object.entries(PLATFORM_REGISTRY).map(([id, meta]) => [id, meta.displayName])
) as Record<PlatformId, string>;

const KIRIN_CLIENT_IDS = new Set<string>(Object.keys(platformLabels));

/**
 * Get the display label for a platform. Known platforms use their registry
 * display name; unknown ids get a capitalised fallback.
 */
export function getPlatformLabel(platform: string): string {
  const normalized = platform.toLowerCase();
  if (Object.hasOwn(platformLabels, normalized)) {
    return platformLabels[normalized as PlatformId];
  }
  return platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase();
}

/**
 * One entry per display label. `copilot` and `github_copilot_cli` both read
 * "GitHub Copilot"; the first id keeps its icon.
 */
export function dedupeByPlatformLabel(platforms: readonly string[]): string[] {
  const seen = new Set<string>();
  return platforms.filter(platform => {
    const label = getPlatformLabel(platform);
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
}

/**
 * Label for a client row: Kirin client IDs (cursor, claude, …) always use the
 * canonical product name. Shadow AI may send a looser display name (e.g. "Claude")
 * that must not override the Kirin mapping for those IDs.
 */
export function getIdeClientDisplayLabel(
  client: string,
  displayName?: string | null
): string {
  const normalized = client.toLowerCase();
  if (KIRIN_CLIENT_IDS.has(normalized)) {
    return getPlatformLabel(normalized);
  }
  return displayName ?? getPlatformLabel(client);
}

/**
 * Get the appropriate icon for a platform. Any platform in the registry
 * returns its branded icon (antigravity maps to `Code2` until a branded SVG
 * ships). Unknown ids fall back to `Code2`.
 */
export function getPlatformIcon(platform: string): IconComponent {
  const normalized = platform.toLowerCase();
  if (!Object.hasOwn(PLATFORM_REGISTRY, normalized)) {
    return Code2 as unknown as IconComponent;
  }
  return PLATFORM_ICONS[normalized as PlatformId];
}
